import { styled } from '@mui/material/styles'
import Button, { ButtonProps } from '@mui/material/Button'
import { buttonColorMap } from './buttonColorMap'

// Initial limit to primary and secondary colors
const brandedColors = [
    'primary',
    'secondary',
    'error',
    'warning',
    'success',
] as const
type BrandedColor = (typeof brandedColors)[number]

const isBrandedColor = (color: unknown): color is BrandedColor =>
    brandedColors.includes(color as BrandedColor)

interface StyledButtonProps extends ButtonProps {
    dense?: boolean
    invert?: boolean
}

export const StyledButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== 'dense' && prop !== 'invert',
})<StyledButtonProps>(
    ({ theme, variant = 'contained', color = 'primary', dense, invert }) => {
        const palette =
            buttonColorMap[color as keyof typeof buttonColorMap] ??
            buttonColorMap.primary

        const base = {
            minWidth: 'unset',
            fontFamily: theme.typography.fontFamily,
            fontWeight: 400,
            textTransform: 'none',
            padding: dense ? '0.5rem' : '0.375rem 0.75rem',
            fontSize: dense ? '0.875rem' : '1rem',
            height: dense ? 32 : 40,
            lineHeight: 'normal',
            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
            '&:hover': {
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            },
        }

        // Let MUI handle non-branded colors for now (info, warning, error, inherit, etc.)
        if (!isBrandedColor(color)) {
            return
        }

        if (variant === 'contained') {
            return {
                ...base,
                backgroundColor: palette.base,
                color: palette.text,
                '&:hover': {
                    ...base['&:hover'],
                    backgroundColor: palette.hover,
                },
                '&:active': {
                    backgroundColor: palette.active,
                },
            }
        }

        if (variant === 'outlined') {
            return {
                ...base,
                backgroundColor: 'transparent',
                color: invert ? palette.base : palette.text,
                border: `1px solid ${palette.border}`,
                '&:hover': {
                    ...base['&:hover'],
                    backgroundColor: palette.base,
                    color: palette.text,
                },
                '&.Mui-focusVisible': palette.focusRing && {
                    boxShadow: `0 0 0 0.2rem ${palette.focusRing}`,
                },
            }
        }

        // text / tertiary
        return {
            ...base,
            color: palette.text,
            '&:hover': {
                ...base['&:hover'],
                backgroundColor: buttonColorMap.light.hover,
            },
        }
    }
)
